import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Toast as BaseToast, type ToastObject } from '@base-ui/react/toast';
import { IconAlertTriangle, IconCircleCheck, IconInfoCircle, IconX } from '@tabler/icons-react';
import { AnimatePresence, motion, useReducedMotion } from 'motion/react';

import { cn } from '../../lib/cn';
import { useTranslation } from '../../i18n/LocaleProvider';
import { DUR, EASE } from '../../interaction';
import { Button } from '../core/Button';
import { IconButton } from '../core/IconButton';

export type ToastVariant = 'success' | 'error' | 'warning' | 'info';
export type ToastPosition = 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';

/**
 * The card's enter/leave transition, read per render so a token change reaches
 * it. The unmount timer reads the SAME token: it used to be a fixed 160ms
 * against a 220ms slide, so the store dropped the card 60ms before its own exit
 * had finished.
 */
function cardTransition(reduceMotion: boolean | null) {
  return reduceMotion ? { duration: 0 } : { duration: DUR.base, ease: EASE.out };
}

const VARIANTS: Record<ToastVariant, { border: string; tone: string; icon: ReactNode }> = {
  success: { border: 'border-success', tone: 'text-success', icon: <IconCircleCheck size={18} /> },
  error: { border: 'border-danger', tone: 'text-danger', icon: <IconAlertTriangle size={18} /> },
  warning: { border: 'border-warning', tone: 'text-warning', icon: <IconAlertTriangle size={18} /> },
  info: { border: 'border-info', tone: 'text-info', icon: <IconInfoCircle size={18} /> },
};

const POSITIONS: Record<ToastPosition, string> = {
  'top-left': 'left-4 top-4',
  'top-right': 'right-4 top-4',
  'bottom-left': 'bottom-4 left-4 flex-col-reverse',
  'bottom-right': 'right-4 bottom-4 flex-col-reverse',
};

export interface ToastAction {
  label: string;
  onClick: () => void;
}

export interface ToastProps {
  variant?: ToastVariant;
  title: ReactNode;
  description?: ReactNode;
  /** Rendered as a real button under the body — e.g. "Undo". */
  action?: ToastAction;
  onClose?: () => void;
  className?: string;
}

/**
 * One toast card. Rendered by `ToastProvider`'s viewport; it must stay inside a
 * `Toast.Root`, which is what names it for assistive technology (`role="dialog"`
 * with `aria-labelledby` from `Toast.Title`,
 * `@base-ui/react/toast/root/ToastRoot.js`).
 */
export function Toast({ variant = 'info', title, description, action, onClose, className }: ToastProps) {
  const { t } = useTranslation();
  const tokens = VARIANTS[variant];

  return (
    <div
      className={cn(
        'pointer-events-auto flex w-full items-start gap-3 rounded-xs border-2 bg-surface p-3 shadow-hard',
        tokens.border,
        className,
      )}
    >
      <span className={cn('mt-0.5 shrink-0', tokens.tone)} aria-hidden="true">
        {tokens.icon}
      </span>
      <div className="min-w-0 flex-1">
        <BaseToast.Title className="font-display text-2xs font-semibold uppercase tracking-label text-heading">
          {title}
        </BaseToast.Title>
        {description ? (
          <BaseToast.Description className="mt-1 text-xs text-body">{description}</BaseToast.Description>
        ) : null}
        {action ? (
          <Button variant="outline" size="sm" className="mt-3" onClick={action.onClick}>
            {action.label}
          </Button>
        ) : null}
      </div>
      {onClose ? (
        <IconButton label={t('ds.toast.dismiss')} icon={<IconX size={14} />} size="sm" onClick={onClose} />
      ) : null}
    </div>
  );
}

interface ToastData {
  variant?: ToastVariant;
  duration?: number;
  action?: ToastAction;
}

export interface ToastOptions {
  title: ReactNode;
  description?: ReactNode;
  variant?: ToastVariant;
  /** Overrides the provider default. `0` keeps it until it is dismissed. */
  duration?: number;
  action?: ToastAction;
  /** `high` is announced immediately (`role="alert"`) instead of politely. */
  priority?: 'low' | 'high';
}

export interface UseToastReturn {
  /** Queues a toast and returns its id. */
  toast: (options: ToastOptions) => string;
  /** Dismisses one toast, or all of them when called with no id. */
  dismiss: (id?: string) => void;
}

/**
 * The imperative API. Everything it creates is a real toast in the provider's
 * store — there is no second list of notifications living in React state.
 */
export function useToast(): UseToastReturn {
  const manager = BaseToast.useToastManager<ToastData>();

  return {
    toast: (options) =>
      manager.add({
        title: options.title,
        description: options.description,
        type: options.variant ?? 'info',
        priority: options.priority,
        // The store's own dismissal timer is switched off (`timeout: 0`); this
        // wrapper schedules it instead, which is what makes pause-on-hover
        // possible per toast. See `ToastItem`.
        timeout: 0,
        data: { variant: options.variant, duration: options.duration, action: options.action },
      }),
    dismiss: (id) => manager.close(id),
  };
}

export interface ToastProviderProps {
  children?: ReactNode;
  position?: ToastPosition;
  /** Visible toasts before the oldest are collapsed into the stack. */
  limit?: number;
  /** Default auto-dismiss delay in ms. `0` means never. */
  duration?: number;
}

/**
 * Mounts the toast viewport. The polite live region itself is Base UI's: its
 * viewport carries `role="region"`, `aria-live="polite"`, `aria-atomic="false"`
 * and `aria-relevant="additions text"`
 * (`@base-ui/react/toast/viewport/ToastViewport.js`), so an added toast is
 * announced without stealing focus.
 */
export function ToastProvider({
  children,
  position = 'bottom-right',
  limit = 3,
  duration = 5000,
}: ToastProviderProps) {
  const { t } = useTranslation();

  return (
    <BaseToast.Provider limit={limit} timeout={0}>
      {children}
      <BaseToast.Portal>
        <BaseToast.Viewport
          aria-label={t('ds.toast.region')}
          className={cn(
            // The top of the stack: --z-toast (800) is above --z-modal (500),
            // which is the point — a toast announcing the result of a dialog is
            // the one thing that must never end up behind it.
            'pointer-events-none fixed z-[var(--z-toast)] flex w-80 max-w-[calc(100vw-2rem)] flex-col gap-2',
            POSITIONS[position],
          )}
        >
          <ToastQueue position={position} duration={duration} />
        </BaseToast.Viewport>
      </BaseToast.Portal>
    </BaseToast.Provider>
  );
}

function ToastQueue({ position, duration }: { position: ToastPosition; duration: number }) {
  const { toasts } = BaseToast.useToastManager<ToastData>();

  return (
    <AnimatePresence initial={false}>
      {toasts.map((item) => (
        <ToastItem key={item.id} toast={item} position={position} fallbackDuration={duration} />
      ))}
    </AnimatePresence>
  );
}

function ToastItem({
  toast,
  position,
  fallbackDuration,
}: {
  toast: ToastObject<ToastData>;
  position: ToastPosition;
  fallbackDuration: number;
}) {
  const manager = BaseToast.useToastManager<ToastData>();
  const reduceMotion = useReducedMotion();
  const duration = toast.data?.duration ?? fallbackDuration;

  const [leaving, setLeaving] = useState(false);
  const [paused, setPaused] = useState(false);
  const remainingRef = useRef(duration);
  const startedAtRef = useRef(0);

  // Pause-on-hover, implemented here because this wrapper owns the timer: the
  // effect's cleanup is the pause. It banks the time left on the clock, and the
  // next run spends exactly that much, so hovering never hands a toast extra
  // time and never eats the remainder. Focus counts as hovering — a keyboard
  // user needs the tip to stay put while they tab to its close button
  // (WCAG 2.2.1).
  useEffect(() => {
    if (paused || leaving || duration <= 0) return undefined;
    startedAtRef.current = Date.now();
    const timer = window.setTimeout(() => setLeaving(true), remainingRef.current);
    return () => {
      window.clearTimeout(timer);
      remainingRef.current = Math.max(remainingRef.current - (Date.now() - startedAtRef.current), 0);
    };
  }, [paused, leaving, duration]);

  // The slide is driven by state rather than by unmount, so the exit animation
  // always has a mounted element to run on; the store is told to drop the toast
  // only once the card has finished leaving.
  useEffect(() => {
    if (!leaving) return undefined;
    // The same token the slide runs on, so the store never drops the card
    // before its own exit has finished.
    const timer = window.setTimeout(() => manager.close(toast.id), reduceMotion ? 0 : DUR.base * 1000);
    return () => window.clearTimeout(timer);
  }, [leaving, reduceMotion, manager, toast.id]);

  const offset = position.endsWith('left') ? -18 : 18;
  const card = reduceMotion
    ? { open: { opacity: 1 }, closed: { opacity: 1 } }
    : { open: { opacity: 1, x: 0 }, closed: { opacity: 0, x: offset } };

  return (
    <BaseToast.Root
      toast={toast}
      // `limited` means the queue limit pushed this toast out of the visible
      // set. Base UI flags it and makes it inert but leaves it rendered
      // (`@base-ui/react/toast/root/ToastRoot.js`), so the queue limit needs
      // this rule to actually be a limit.
      className="pointer-events-auto data-[limited]:hidden"
      onPointerEnter={() => setPaused(true)}
      onPointerLeave={() => setPaused(false)}
      onFocusCapture={() => setPaused(true)}
      onBlurCapture={() => setPaused(false)}
      render={
        <motion.div
          initial={reduceMotion ? false : { opacity: 0, x: offset }}
          // `ending` covers a dismissal that did not come from this card — a
          // `dismiss()` call — so that path slides out too.
          animate={leaving || toast.transitionStatus === 'ending' ? 'closed' : 'open'}
          variants={card}
          transition={cardTransition(reduceMotion)}
        />
      }
    >
      <Toast
        variant={toast.data?.variant ?? 'info'}
        title={toast.title}
        description={toast.description}
        action={toast.data?.action}
        onClose={() => setLeaving(true)}
      />
    </BaseToast.Root>
  );
}
